import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { AuthProvider } from './api/AuthProvider';
import { AuthedApiProvider } from './api/AuthedApiProvider';
import { resolveRuntimeConfig } from './api/config';
import { mockFetch } from './mocks/fetch';
import { ProposedApiProvider } from './proposed/ProposedApiProvider';
import { App } from './components/App';
import { createMaskedHistory } from './lib/masked-history';
import { initBoardTheme } from './board/theme';
// Chessground board styling (§6.2). The engine ships without these; omitting
// them leaves the board as an empty box — no squares, pieces, or coordinates.
import '@lichess-org/chessground/assets/chessground.base.css';
import '@lichess-org/chessground/assets/chessground.cburnett.css';
import './styles.css';
import './studio.css';
// The one board skin (colours from src/board/theme.ts); replaces Chessground's brown theme.
import './board.css';
import './review.css';

// Static SPA (specification §6.2): Node tooling is build-time only. The default
// build talks to the real backend at VITE_API_ORIGIN; the §4 mock fetch is
// opt-in via VITE_USE_MOCK=1.
const runtime = resolveRuntimeConfig(import.meta.env);
const queryClient = new QueryClient();
// Routes live in history state; the address bar only ever shows the site root.
const history = createMaskedHistory(window, import.meta.env.BASE_URL);

// Paint every board in the saved skin from the first frame.
initBoardTheme();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root element');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <HistoryRouter history={history}>
        <AuthProvider apiOrigin={runtime.apiOrigin}>
          <AuthedApiProvider runtime={runtime} mockFetch={runtime.useMock ? mockFetch : undefined}>
            <ProposedApiProvider enabled={runtime.useMock}>
              <App />
            </ProposedApiProvider>
          </AuthedApiProvider>
        </AuthProvider>
      </HistoryRouter>
    </QueryClientProvider>
  </StrictMode>,
);
